"use client";

import React, { useState } from "react";
import { 
  Stethoscope, 
  Calendar, 
  Clock, 
  Activity, 
  UserCheck, 
  ArrowRight, 
  Sparkles,
  ShieldCheck,
  CreditCard
} from "lucide-react";
import { DentalPatientLead } from "@/lib/demos/dentalDb";

interface DentalKanbanProps {
  patients: DentalPatientLead[];
  onSelectPatient: (p: DentalPatientLead) => void;
  onUpdateStage: (patientId: number, newStage: DentalPatientLead["stage"]) => void;
  currency?: "INR" | "USD";
}

interface StageConfig {
  id: DentalPatientLead["stage"];
  title: string;
  dotColor: string;
  description: string;
}

const DENTAL_STAGES: StageConfig[] = [
  {
    id: "Consultation Booked",
    title: "Consultation Booked",
    dotColor: "bg-blue-400",
    description: "New web clinical consultation inquiries",
  },
  {
    id: "Preliminary 3D Scan Scheduled",
    title: "3D Scan Scheduled",
    dotColor: "bg-teal-400",
    description: "iTero scan set & appointment pass dispatched",
  },
  {
    id: "Treatment Plan Presented",
    title: "Treatment Plan Presented",
    dotColor: "bg-cyan-400",
    description: "Diagnostic smile design & estimate presented",
  },
  {
    id: "In Treatment / Active Procedure",
    title: "In Treatment (Active)",
    dotColor: "bg-amber-400",
    description: "Chairside clinical procedure ongoing",
  },
  {
    id: "Procedure Completed (Won)",
    title: "Procedure Completed",
    dotColor: "bg-emerald-400",
    description: "Dues settled & warranty certificate issued",
  },
  {
    id: "6-Month Recall Scheduled",
    title: "6-Month Recall Queued",
    dotColor: "bg-indigo-400",
    description: "Preventive hygiene & check-up recall",
  },
];

export default function DentalKanban({
  patients,
  onSelectPatient,
  onUpdateStage,
  currency = "INR",
}: DentalKanbanProps) {
  const [draggedId, setDraggedId] = useState<number | null>(null);

  const handleDragStart = (e: React.DragEvent, id: number) => {
    e.dataTransfer.setData("text/plain", id.toString());
    setDraggedId(id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent, targetStage: DentalPatientLead["stage"]) => {
    e.preventDefault();
    const idStr = e.dataTransfer.getData("text/plain");
    const id = parseInt(idStr, 10);
    if (!isNaN(id)) {
      onUpdateStage(id, targetStage);
    }
    setDraggedId(null);
  };

  const sym = currency === "USD" ? "$" : "₹";

  return (
    <div className="flex gap-4 overflow-x-auto pb-6 pt-2 select-none scrollbar-thin scrollbar-thumb-slate-800">
      {DENTAL_STAGES.map((col) => {
        const colPatients = patients.filter((p) => p.stage === col.id);
        const colRevenue = colPatients.reduce((sum, p) => sum + (p.estimated_cost || 0), 0);

        return (
          <div
            key={col.id}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, col.id)}
            className="w-80 shrink-0 bg-slate-950/80 rounded-2xl border border-slate-800/90 flex flex-col max-h-[calc(100vh-230px)] shadow-xl"
          >
            {/* Header */}
            <div className="p-3.5 border-b border-slate-800/80 bg-slate-900/60 rounded-t-2xl space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2.5 h-2.5 rounded-full ${col.dotColor}`} />
                  <h3 className="text-xs font-bold text-white tracking-tight">{col.title}</h3>
                </div>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-slate-800 text-slate-300">
                  {colPatients.length}
                </span>
              </div>

              <div className="flex items-center justify-between text-[11px] text-slate-400">
                <span>{colPatients.length} Patients</span>
                <span className="font-semibold text-teal-400">{sym}{colRevenue.toLocaleString()}</span>
              </div>
            </div>

            {/* Cards List */}
            <div className="p-3 overflow-y-auto space-y-3 flex-1 scrollbar-thin scrollbar-thumb-slate-800">
              {colPatients.length === 0 ? (
                <div className="h-28 border-2 border-dashed border-slate-800/70 rounded-xl flex items-center justify-center text-xs text-slate-400">
                  Drop patient records here
                </div>
              ) : (
                colPatients.map((patient) => (
                  <div
                    key={patient.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, patient.id)}
                    onClick={() => onSelectPatient(patient)}
                    className="p-4 rounded-xl bg-slate-900/90 border border-slate-800 hover:border-teal-500/40 cursor-grab active:cursor-grabbing hover:shadow-lg transition-all duration-200 space-y-3 group"
                  >
                    {/* Top Row: Name & PRN */}
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="text-xs font-bold text-white group-hover:text-teal-300 transition-colors">
                          {patient.patient_name}
                        </h4>
                        <span className="text-[11px] text-slate-400 block truncate max-w-[170px]">
                          {patient.email}
                        </span>
                      </div>

                      <span className="text-[10px] font-bold text-teal-400 bg-teal-500/10 px-2 py-0.5 rounded border border-teal-500/20 shrink-0">
                        PRN #{patient.id}
                      </span>
                    </div>

                    {/* Treatment Focus & Schedule */}
                    <div className="p-2 bg-slate-950/80 rounded-lg border border-slate-800/80 text-[11px] text-slate-300 space-y-1">
                      <div className="flex items-center gap-1 text-teal-300 font-semibold truncate">
                        <Stethoscope className="w-3 h-3 shrink-0" />
                        <span className="truncate">{patient.treatment_interest}</span>
                      </div>
                      <div className="flex items-center justify-between text-slate-400 pt-0.5">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-slate-500" />
                          {patient.scan_date ? patient.scan_date.split(" (")[0] : "Scan Pending"}
                        </span>
                      </div>
                    </div>

                    {/* Doctor & Cost */}
                    <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between text-[11px]">
                      <span className="text-slate-400 truncate max-w-[160px]">
                        {patient.preferred_doctor.split(" (")[0]}
                      </span>
                      <span className="font-bold text-teal-400 shrink-0">
                        {sym}{patient.estimated_cost.toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
